// Explore-stats page: Committee membership statistics.
// Recreates the Handbook page — per-member committee service derived from the
// committeemembers index. Charts: longest service on a single committee (with
// a Cumulative/Continuous toggle) and the parliamentarians on the most
// committees. Table lists committee type, committee, parliamentarian,
// cumulative service and longest continuous service (names link to detail
// pages by PHID/CID via the shared scaffold).

import React, { useState, useEffect } from 'react';
import {
  Paper, Typography, Box, Grid, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import { getExploreStat } from '../../services/api';
import ExploreStatsPage from './ExploreStatsPage';
import { StatBar } from './charts';

export default function CommitteeMembership() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mode, setMode] = useState('cumulative');

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await getExploreStat('committee-membership');
        if (active) setData(res);
      } catch (e) {
        if (active) setError(e.message || 'Failed to load statistics');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const c = data?.charts || {};
  const longest = mode === 'continuous'
    ? (c.longest_continuous || [])
    : (c.longest_cumulative || []);

  return (
    <ExploreStatsPage
      title="Committee membership statistics"
      description={`Parliamentary committee service by member${
        c.total ? ` (${c.total} member–committee records)` : ''
      } — the longest service on a single committee and the parliamentarians who served on the most committees.`}
      loading={loading}
      error={error}
      columns={data?.columns || []}
      rows={data?.table_rows || []}
      links={data?._links || []}
      csvName="committee-membership"
    >
      {data && (
        <>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center',
                           justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                  <Typography variant="h6">
                    Longest service on a single committee
                  </Typography>
                  <ToggleButtonGroup size="small" exclusive value={mode}
                                     onChange={(e, v) => v && setMode(v)}>
                    <ToggleButton value="cumulative">Cumulative</ToggleButton>
                    <ToggleButton value="continuous">Continuous</ToggleButton>
                  </ToggleButtonGroup>
                </Box>
                <StatBar data={longest} xKey="name" yKey="years"
                         layout="vertical" height={400} />
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Parliamentarians on the most committees
                </Typography>
                <StatBar data={c.most_committees || []} xKey="name"
                         yKey="committees" layout="vertical" height={400} />
              </Paper>
            </Grid>
          </Grid>
        </>
      )}
    </ExploreStatsPage>
  );
}
